import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

import { submitClientInquiry } from '../../services/clientRequest.service';

const BUDGET_OPTIONS = [
  '$1,000 - $5,000',
  '$5,000 - $15,000',
  '$15,000+',
  'To be discussed',
];

const TIMELINE_OPTIONS = [
  'Urgent (< 1 month)',
  '1-3 months',
  '3-6 months',
  'Flexible',
];

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  company: '',
  projectScope: '',
  budget: '',
  timeline: '',
  meetingDate: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SCOPE_MIN = 30;

// Field-level validators. Return an error string, or '' when valid.
const validators = {
  fullName: (v) => (v.trim() ? '' : 'Full name is required.'),
  email: (v) => {
    if (!v.trim()) return 'Email address is required.';
    return EMAIL_RE.test(v.trim()) ? '' : 'Enter a valid email address.';
  },
  projectScope: (v) => {
    if (!v.trim()) return 'Please describe your project.';
    return v.trim().length < SCOPE_MIN
      ? `Please add a bit more detail (${v.trim().length}/${SCOPE_MIN} characters).`
      : '';
  },
};

const validateAll = (form) =>
  Object.entries(validators).reduce((acc, [field, fn]) => {
    const message = fn(form[field] ?? '');
    if (message) acc[field] = message;
    return acc;
  }, {});

export default function ClientRequestForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [serverError, setServerError] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [copied, setCopied] = useState(false);

  const runFieldValidation = (name, value) => {
    if (!validators[name]) return;
    setErrors((prev) => ({ ...prev, [name]: validators[name](value) }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) runFieldValidation(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    runFieldValidation(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    const validationErrors = validateAll(form);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setTouched({ fullName: true, email: true, projectScope: true });
      return;
    }

    setStatus('submitting');

    const payload = {
      client_name: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company_name: form.company.trim(),
      project_scope: form.projectScope.trim(),
      estimated_budget: form.budget,
      target_timeline: form.timeline,
    };
    // Backend expects a datetime; only send when the user picked one.
    if (form.meetingDate) {
      payload.preferred_meeting_date = new Date(form.meetingDate).toISOString();
    }

    try {
      const response = await submitClientInquiry(payload);
      setTrackingCode(response.data?.tracking_code || '');
      setStatus('success');
    } catch (err) {
      const data = err?.response?.data;
      // DRF returns field errors at the top level ({ email: [...] }) and uses
      // `detail` for auth/throttle responses. Support both cleanly.
      const fieldErrors =
        data && typeof data === 'object' && !data.detail ? data : null;

      let mapped = {};
      if (fieldErrors) {
        const fieldMap = {
          client_name: 'fullName',
          email: 'email',
          project_scope: 'projectScope',
        };
        Object.entries(fieldErrors).forEach(([key, val]) => {
          const local = fieldMap[key];
          if (local) mapped[local] = Array.isArray(val) ? val[0] : String(val);
        });
        if (Object.keys(mapped).length) setErrors((prev) => ({ ...prev, ...mapped }));
      }

      const message =
        data?.detail ||
        (Object.keys(mapped).length
          ? 'Please fix the highlighted fields and try again.'
          : 'We couldn’t submit your request. Please check your connection and try again.');

      setServerError(message);
      setStatus('error');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trackingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setTouched({});
    setServerError('');
    setTrackingCode('');
    setCopied(false);
    setStatus('idle');
  };

  const isSubmitting = status === 'submitting';

  // ── Success view ────────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div className="w-full bg-white pb-24 pt-28 dark:bg-dark-bg sm:pt-36">
        <div className="mx-auto max-w-xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-dark-card sm:p-12"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-secondary/25 blur-3xl" />

            <div className="relative">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/15">
                <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>

              <h2 className="mt-6 text-2xl font-bold text-primary dark:text-dark-text">
                Meeting Request Received!
              </h2>

              <p className="mt-2 text-sm text-gray-500 dark:text-dark-muted">
                Keep this tracking code — it’s your reference for this request.
              </p>

              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <span className="inline-flex items-center rounded-lg border border-secondary/40 bg-secondary/10 px-4 py-2 font-mono text-lg font-semibold tracking-wide text-primary dark:text-dark-text">
                  {trackingCode}
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-white/15 dark:text-dark-text dark:hover:bg-white/5"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>

              <p className="mx-auto mt-8 max-w-md text-sm leading-relaxed text-gray-600 dark:text-dark-muted">
                Our team at <span className="font-semibold text-primary dark:text-dark-text">Afroawi Technologies</span> will
                review your scope and get back to you within 24 hours.
              </p>

              <button
                type="button"
                onClick={resetForm}
                className="mt-8 inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 dark:focus:ring-offset-dark-card"
              >
                Submit Another Request
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Form view ───────────────────────────────────────────────────────────────
  const inputBase =
    'w-full rounded-lg border bg-white px-4 py-3 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary/70 dark:bg-dark-bg dark:text-dark-text dark:placeholder:text-dark-muted';
  const inputBorder = (field) =>
    errors[field] && touched[field]
      ? 'border-red-400 focus:ring-red-300'
      : 'border-gray-300 dark:border-white/15';

  const FieldError = ({ field }) =>
    errors[field] && touched[field] ? (
      <p className="mt-1.5 text-xs text-red-500">{errors[field]}</p>
    ) : null;

  return (
    <div className="w-full bg-white pb-24 pt-28 dark:bg-dark-bg sm:pt-36">
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-dark-card sm:p-12"
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-secondary/20 blur-3xl" />

          {/* Header */}
          <div className="relative mb-8">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2 w-2 flex-none rounded-sm bg-secondary" aria-hidden="true" />
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-dark">
                Start a project
              </p>
            </div>
            <h1 className="text-3xl font-bold text-primary dark:text-dark-text">
              Request a Meeting
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-dark-muted">
              Tell us about your project and pick a time to talk. We’ll review your scope and
              respond within 24 hours.
            </p>
          </div>

          {/* Network / server error banner */}
          {status === 'error' && serverError && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              className="relative mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
            >
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-none" />
              <span>{serverError}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} noValidate className="relative space-y-5">
            {/* Full name + Email */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="fullName" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={form.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Jane Doe"
                  aria-invalid={Boolean(errors.fullName && touched.fullName)}
                  className={`${inputBase} ${inputBorder('fullName')}`}
                />
                <FieldError field="fullName" />
              </div>

              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="jane@company.com"
                  aria-invalid={Boolean(errors.email && touched.email)}
                  className={`${inputBase} ${inputBorder('email')}`}
                />
                <FieldError field="email" />
              </div>
            </div>

            {/* Phone + Company */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                  Phone / Telegram <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="text"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+251 900 000 000 or @username"
                  className={`${inputBase} ${inputBorder('phone')}`}
                />
              </div>

              <div>
                <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                  Company / Organization <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Acme Inc."
                  className={`${inputBase} ${inputBorder('company')}`}
                />
              </div>
            </div>

            {/* Project scope */}
            <div>
              <label htmlFor="projectScope" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                Project Overview &amp; Scope <span className="text-red-500">*</span>
              </label>
              <textarea
                id="projectScope"
                name="projectScope"
                rows={5}
                value={form.projectScope}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="What are you building? Goals, key features, platforms, and anything that helps us understand the work…"
                aria-invalid={Boolean(errors.projectScope && touched.projectScope)}
                className={`${inputBase} resize-y ${inputBorder('projectScope')}`}
              />
              <div className="mt-1.5 flex items-center justify-between">
                <FieldError field="projectScope" />
                <span className="ml-auto text-xs text-gray-400 dark:text-dark-muted">
                  {form.projectScope.trim().length}/{SCOPE_MIN} min
                </span>
              </div>
            </div>

            {/* Budget + Timeline */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="budget" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                  Estimated Budget Range
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  className={`${inputBase} ${inputBorder('budget')}`}
                >
                  <option value="">Select a range…</option>
                  {BUDGET_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="timeline" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                  Target Timeline
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={form.timeline}
                  onChange={handleChange}
                  className={`${inputBase} ${inputBorder('timeline')}`}
                >
                  <option value="">Select a timeline…</option>
                  {TIMELINE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Preferred meeting date */}
            <div>
              <label htmlFor="meetingDate" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-dark-text">
                Preferred Meeting Date &amp; Time <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="meetingDate"
                name="meetingDate"
                type="datetime-local"
                value={form.meetingDate}
                onChange={handleChange}
                className={`${inputBase} ${inputBorder('meetingDate')}`}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 dark:focus:ring-offset-dark-card sm:w-auto"
            >
              {isSubmitting ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  Submitting Request…
                </>
              ) : (
                'Send Meeting Request'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
